import React, { useMemo } from 'react';

import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';

import { formatDate } from '../../lib/helpers/date.helpers';
import { usePlayerReportCard, useReportCardSurface } from '../../lib/services/report-card-access.service';
import { useCompanySportTerminology } from '../../lib/stores/company.store';
import { IntlMsg } from '../_core/IntlMsg/intl-msg.component';
import { SectionHeading } from '../_core/_ui/typography.component';
import { reportCardStatusCaptions } from './report-card-access.helpers';
import { getMessages } from './report-card-access.messages';
import { ReportCardActions } from './report-card-actions.component';
import { ReportCardActionsProvider } from './report-card-actions.context';
import { ReportCardStatusChip } from './report-card-status-chip.component';
import { usePlayerReportCardSectionStyles } from './player-report-card-section.styles';

interface IPlayerReportCardSectionProps {
  playerAssessmentId: string;
  ageGroupId: string;
}

// Mounted when useReportCardSurface says sectionVisible; the actions check their own roles.
export const PlayerReportCardSection: React.FC<IPlayerReportCardSectionProps> = ({ playerAssessmentId, ageGroupId }) => {
  const classes = usePlayerReportCardSectionStyles();
  const termLookup = useCompanySportTerminology();
  const messages = useMemo(() => getMessages(termLookup), [termLookup]);
  const { associationOff, canManage } = useReportCardSurface(ageGroupId);
  const captions = useMemo(
    () => reportCardStatusCaptions(messages, { associationOff, canManage }),
    [messages, associationOff, canManage],
  );
  const { playerAssessment, loading, error } = usePlayerReportCard(playerAssessmentId);

  const access = playerAssessment?.reportCardAccess;
  const releasedOn = useMemo(() => (access?.releasedAt ? formatDate(access.releasedAt) : null), [access]);
  const revokedOn = useMemo(() => (access?.status === 'REVOKED' && access.revokedAt ? formatDate(access.revokedAt) : null), [access]);
  const caption = access && captions[access.status];

  return (
    <ReportCardActionsProvider>
      <div className={classes.header}>
        <SectionHeading><IntlMsg {...messages.lblReportCard} /></SectionHeading>
        {playerAssessment && (
          <div className={classes.actions}>
            <ReportCardActions playerAssessment={playerAssessment} />
          </div>
        )}
      </div>

      {associationOff && (
        <Typography variant="body2" color="textSecondary"><IntlMsg {...messages.captionAssociationOff} /></Typography>
      )}

      {!access && loading && <CircularProgress size={24} />}
      {!access && !loading && error && (
        <Typography variant="body2" color="error"><IntlMsg {...messages.errStatusNotLoaded} /></Typography>
      )}

      {access && (
        <>
          <div className={classes.status}>
            <ReportCardStatusChip access={access} />
            {releasedOn && (
              <Typography variant="body2" color="textSecondary"><IntlMsg {...messages.msgSentOn} values={{ date: releasedOn }} /></Typography>
            )}
            {revokedOn && (
              <Typography variant="body2" color="textSecondary"><IntlMsg {...messages.msgRevokedOn} values={{ date: revokedOn }} /></Typography>
            )}
          </div>
          {caption && <Typography variant="body2"><IntlMsg {...caption} /></Typography>}
        </>
      )}
    </ReportCardActionsProvider>
  );
};
